<template>
  <div>
    <!-- Pantalla de carga / logo centrado cuando no hay datos -->
    <section
      v-if="pase !== 2"
      class="hero"
      :class="{ 'hero-fullheight': !hasEmployee }"
    >
      <div class="hero-body">
        <div class="container has-text-centered">
          <img
            :width="!hasEmployee ? '300px' : '180px'"
            src="@/assets/logo2.png"
            alt="Logo"
          />
        </div>
      </div>
    </section>

    <section class="section" v-if="hasEmployee">
      <div class="container">
        <!-- Pill flotante: cambia según el pase activo -->
        <div class="has-text-centered survey-tag-wrap">
          <p class="survey-tag" v-if="pase === 0">CL7 · CLIMA LABORAL</p>
          <p class="survey-tag" v-if="pase === 1">Instrucciones</p>
          <p class="survey-tag" v-if="pase === 2">Preguntas</p>
          <p class="survey-tag" v-if="pase === 3">Comentarios</p>
        </div>

        <div class="box survey-box">
          <!-- ──────────────────────────────────────────
               PASE 0 — Consentimiento
          ────────────────────────────────────────── -->
          <div v-show="pase === 0">
            <div class="has-text-centered mb-4">
              <h1 class="title is-3">¡Hola, {{ employeeresponded.nombre }}!</h1>
            </div>

            <div class="consent-block">
              <p>
                Tu participación es <strong>voluntaria</strong>. No se
                reportarán resultados individuales y tus respuestas se manejarán
                de manera <strong>confidencial</strong>, según la normatividad
                vigente en Colombia (Ley 1581 de 2012).
              </p>
              <p>
                Estos datos podrán ser usados por Siete S.A.S. para
                investigación, manteniendo la confidencialidad de los mismos.
              </p>
              <p>
                Teniendo en cuenta estas condiciones, das tu consentimiento para
                continuar.
              </p>
            </div>

            <div class="field consent-radios mt-5">
              <b-radio
                v-model="responses.agree"
                :native-value="true"
                type="is-dark"
              >
                Sí, acepto
              </b-radio>
              <b-radio
                v-model="responses.agree"
                :native-value="false"
                type="is-dark"
                class="ml-4"
              >
                No acepto
              </b-radio>
            </div>

            <div class="has-text-centered mt-5">
              <b-button
                v-if="responses.agree"
                type="is-dark"
                size="is-medium"
                icon-right="arrow-right"
                @click="next()"
              >
                Continuar
              </b-button>
              <b-button v-else type="is-dark" size="is-medium" @click="endno()">
                Terminar y guardar
              </b-button>
            </div>
          </div>

          <!-- ──────────────────────────────────────────
               PASE 1 — Instrucciones
          ────────────────────────────────────────── -->
          <div v-show="pase === 1">
            <div class="content instructions-content">
              <p>
                A continuación encontrarás distintas frases sobre tu
                organización, tu trabajo o tu jefe inmediato. Lee cada una con
                atención y selecciona el punto que mejor represente tu nivel de
                acuerdo.
              </p>
              <p>
                No hay respuestas buenas o malas. Todas son valiosas si reflejan
                tu experiencia de forma honesta.
                <strong
                  >Entre más sincera sea tu respuesta, más nos ayudas a
                  mejorar.</strong
                >
              </p>
            </div>

            <!-- Escala visual de referencia: grilla compacta 3x2 -->
            <div class="scale-grid mt-4">
              <div
                v-for="item in scaleLabels"
                :key="item.value"
                class="scale-grid-item"
                :class="{ 'scale-grid-item--nose': item.value === 0 }"
              >
                <span v-if="item.value !== 0" class="scale-grid-value">{{
                  item.value
                }}</span>
                <span class="scale-grid-label">{{ item.label }}</span>
              </div>
            </div>

            <div class="has-text-centered mt-5">
              <b-button
                type="is-dark"
                size="is-medium"
                icon-right="arrow-right"
                @click="next()"
              >
                Comenzar encuesta
              </b-button>
            </div>
          </div>

          <!-- ──────────────────────────────────────────
               PASE 2 — Preguntas
          ────────────────────────────────────────── -->
          <div v-show="pase === 2">
            <!-- Vista móvil -->
            <div v-if="isMovil">
              <div
                v-for="question in displayedQuestions"
                :key="question.codigo"
                class="question-row"
              >
                <p class="question-row__text">
                  <span class="question-row__num"
                    >{{ question.numeroPregunta }}.</span
                  >
                  {{ question.question }}
                </p>
                <div
                  class="option-grid option-grid--compact"
                  role="radiogroup"
                  :aria-label="question.question"
                >
                  <b-radio-button
                    v-for="opt in scaleOptions"
                    :key="opt.value"
                    v-model="responses[question.codigo]"
                    :native-value="String(opt.value)"
                    :name="question.codigo"
                  >
                    <span v-if="opt.value !== 0" class="option-card__value">{{
                      opt.value
                    }}</span>
                    <span class="option-card__label">{{ opt.label }}</span>
                  </b-radio-button>
                </div>
              </div>
            </div>

            <!-- Vista escritorio -->
            <div v-else>
              <!-- Filas de preguntas -->
              <div
                v-for="question in displayedQuestions"
                :key="question.codigo"
                class="question-row"
              >
                <p class="question-row__text">
                  <span class="question-row__num"
                    >{{ question.numeroPregunta }}.</span
                  >
                  {{ question.question }}
                </p>
                <div
                  class="option-grid option-grid--full"
                  role="radiogroup"
                  :aria-label="question.question"
                >
                  <b-radio-button
                    v-for="opt in scaleOptions"
                    :key="opt.value"
                    v-model="responses[question.codigo]"
                    :native-value="String(opt.value)"
                    :name="question.codigo"
                  >
                    <span v-if="opt.value !== 0" class="option-card__value">{{
                      opt.value
                    }}</span>
                    <span class="option-card__label">{{ opt.label }}</span>
                  </b-radio-button>
                </div>
              </div>
            </div>

            <!-- Espaciador para que la última pregunta no quede tapada por el footer fijo -->
            <div class="footer-spacer"></div>
          </div>

          <!-- ──────────────────────────────────────────
               PASE 3 — Comentarios y cierre
          ────────────────────────────────────────── -->
          <div v-show="pase === 3">
            <div class="has-text-centered mb-5">
              <p class="survey-tag">¡Ya casi terminamos!</p>
              <h2 class="title is-4 mt-2">¿Tienes algo que agregar?</h2>
              <p class="subtitle is-6 has-text-grey">
                Este espacio es opcional y confidencial.
              </p>
            </div>

            <b-field label="Comentarios">
              <b-input
                type="textarea"
                v-model="responses.comentarios"
                placeholder="Escribe aquí tus comentarios, sugerencias o lo que quieras compartir..."
                rows="5"
              ></b-input>
            </b-field>

            <div class="level mt-4">
              <div class="level-left">
                <b-button
                  v-if="anteriorshow"
                  icon-left="arrow-left"
                  @click="previousPase()"
                >
                  Anterior
                </b-button>
              </div>
              <div class="level-right">
                <b-button
                  type="is-dark"
                  size="is-medium"
                  icon-right="check"
                  @click="end()"
                >
                  Finalizar encuesta
                </b-button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ──────────────────────────────────────────
         Footer fijo: progreso + navegación (solo durante las preguntas)
    ────────────────────────────────────────── -->
    <div v-if="pase === 2" class="survey-footer">
      <div class="survey-footer__inner">
        <div class="progress-wrap">
          <div class="progress-info">
            <span>Página {{ page }} de {{ numberOfPages }}</span>
            <span>{{ Math.round((page / numberOfPages) * 100) }}%</span>
          </div>
          <progress
            class="progress is-dark is-small"
            :value="(page / numberOfPages) * 100"
            max="100"
          ></progress>
        </div>

        <div class="level mt-3">
          <div class="level-left">
            <b-button
              v-if="page !== 1"
              icon-left="arrow-left"
              @click="previousPage()"
            >
              Anterior
            </b-button>
          </div>
          <div class="level-right">
            <b-button
              v-if="page !== numberOfPages"
              type="is-dark"
              icon-right="arrow-right"
              :disabled="disabledbutton()"
              @click="nextquestions()"
            >
              Siguiente
            </b-button>
            <b-button
              v-else
              type="is-dark"
              icon-right="arrow-right"
              :disabled="disabledbutton1()"
              @click="next()"
            >
              Continuar
            </b-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import apiServices from "@/services/yungol-services-public";
import { mapState } from "vuex";

// Escala de respuesta: de mayor a menor acuerdo + "No sé"
const SCALE_OPTIONS = [
  { value: 5, label: "Completamente de acuerdo", highlight: true },
  { value: 4, label: "De acuerdo", highlight: false },
  { value: 3, label: "Ligeramente de acuerdo", highlight: false },
  { value: 2, label: "En desacuerdo", highlight: false },
  { value: 1, label: "Completamente en desacuerdo", highlight: true },
  { value: 0, label: "No sé", highlight: true },
];

const QUESTIONS = [
  {
    codigo: "seg37",
    question:
      "Aquí las personas siguen todos los procedimientos de seguridad y salud en el trabajo.",
  },
  {
    codigo: "comp90",
    question: "Aquí dan mejores beneficios que en otras organizaciones.",
  },
  {
    codigo: "logr66",
    question:
      "En esta organización pareciera que no importa alcanzar los resultados.",
  },
  {
    codigo: "rec102",
    question:
      "En esta organización faltan recursos que son indispensables para hacer el trabajo.",
  },
  {
    codigo: "seg40",
    question:
      "Aquí el trabajo se planea teniendo en cuenta la salud y seguridad de las personas.",
  },
  {
    codigo: "apo21",
    question: "Aquí se ignoran las necesidades de las personas.",
  },
  {
    codigo: "vinc14",
    question: "Las personas están desconectadas de la organización.",
  },
  {
    codigo: "rep74",
    question: "Esta organización es muy bien reconocida en el medio.",
  },
  { codigo: "aps1", question: "Aquí la gente se ayuda entre sí." },
  {
    codigo: "apo18",
    question:
      "En esta organización se hacen cosas por el bienestar de las personas.",
  },
  {
    codigo: "serv73",
    question:
      "En esta organización se ignoran las necesidades de los clientes/usuarios.",
  },
  {
    codigo: "seg38",
    question:
      "En esta organización los directivos promueven el trabajo seguro.",
  },
  { codigo: "lid83", question: "Mi jefe coordina bien el trabajo." },
  { codigo: "enga108", question: "Me siento muy conectado(a) con mi trabajo." },
  {
    codigo: "logr65",
    question: "En mi trabajo la gente persiste hasta alcanzar los resultados.",
  },
  { codigo: "enga104", question: "Me entusiasma mucho mi trabajo." },
  {
    codigo: "logr63",
    question: "Aquí la gente trabaja para alcanzar las metas planteadas.",
  },
  {
    codigo: "exc28",
    question:
      "En esta organización la gente hace las cosas de cualquier manera.",
  },
  {
    codigo: "cyc29",
    question: "Aquí las personas conocen claramente su cargo.",
  },
  {
    codigo: "cyc34",
    question: "Aquí la gente sabe bien qué es lo que debe hacer.",
  },
  {
    codigo: "comp94",
    question: "En esta organización los salarios son justos.",
  },
  { codigo: "cyc35", question: "Aquí la gente desconoce qué debe hacer." },
  { codigo: "cyc31", question: "Aquí las personas conocen las normas." },
  {
    codigo: "rep76",
    question: "A mucha gente le gustaría trabajar en esta organización.",
  },
  { codigo: "agi49", question: "Esta organización se adapta rápidamente." },
  {
    codigo: "aps2",
    question: "En esta organización las personas ayudan a los demás.",
  },
  {
    codigo: "inn60",
    question: "Aquí nunca se harán las cosas de manera distinta.",
  },
  {
    codigo: "enga109",
    question: "Cuando pienso en mi trabajo me da mucha pereza.",
  },
  { codigo: "lid86", question: "Mi jefe nos motiva a ser innovadores." },
  {
    codigo: "rec100",
    question:
      "En esta organización tenemos todo lo necesario para hacer bien el trabajo.",
  },
  {
    codigo: "agi53",
    question: "Aquí la gente es lenta para adaptarse a los cambios.",
  },
  {
    codigo: "rep78",
    question: "La gente considera que esta es una organización muy buena.",
  },
  {
    codigo: "rec101",
    question:
      "En esta organización, las personas reciben oportunamente sus implementos de trabajo.",
  },
  { codigo: "agi52", question: "Esta organización se anticipa a los cambios." },
  { codigo: "apo17", question: "Aquí se aprecian los esfuerzos de la gente." },
  {
    codigo: "aps7",
    question: "En esta organización la gente evita ayudar a otros.",
  },
  { codigo: "rep79", question: "La organización tiene mala reputación." },
  {
    codigo: "apo20",
    question:
      "En esta organización se respeta la vida extralaboral de las personas.",
  },
  { codigo: "lid80", question: "Mi jefe es una persona cercana a su equipo." },
  {
    codigo: "exc22",
    question: "En esta organización se trabaja pensando en la excelencia.",
  },
  {
    codigo: "aut44",
    question: "Aquí las personas pueden tomar decisiones en su trabajo.",
  },
  { codigo: "exc26", question: "Aquí la gente trabaja con alta calidad." },
  {
    codigo: "agi51",
    question:
      "En esta organización se actúa rápidamente ante situaciones inesperadas.",
  },
  {
    codigo: "rec98",
    question:
      "En esta organización le dan a la gente lo que necesita para hacer su trabajo.",
  },
  {
    codigo: "aut42",
    question: "Aquí la gente puede hacer las cosas con autonomía.",
  },
  {
    codigo: "inn54",
    question:
      "Aquí se trabaja continuamente para mejorar los procesos y procedimientos.",
  },
  {
    codigo: "inn55",
    question: "En esta organización se crean nuevos productos/servicios.",
  },
  {
    codigo: "aps5",
    question: "Aquí la gente encuentra quien le ayude cuando lo necesita.",
  },
  {
    codigo: "serv69",
    question:
      "Satisfacer a los clientes es un objetivo importante para todas las personas de esta organización.",
  },
  {
    codigo: "comp91",
    question: "Aquí los cargos similares tienen beneficios similares.",
  },
  {
    codigo: "logr62",
    question: "En esta organización se definen metas retadoras.",
  },
  {
    codigo: "serv68",
    question:
      "En esta organización se trabaja para satisfacer todas las expectativas de los clientes/usuarios.",
  },
  { codigo: "lid88", question: "Tengo un mal jefe." },
  { codigo: "comp95", question: "En mi trabajo hay inequidad salarial." },
  { codigo: "enga106", question: "El tiempo en mi trabajo se pasa volando." },
  {
    codigo: "exc24",
    question: "En esta organización se reconoce el desempeño excelente.",
  },
  {
    codigo: "aut46",
    question:
      "En esta organización se valora que las personas puedan decidir por sí mismas.",
  },
  {
    codigo: "vinc11",
    question:
      "Las personas se sienten orgullosas de pertenecer a esta organización.",
  },
  {
    codigo: "aut47",
    question:
      "En mi trabajo, las personas deben consultar antes de tomar cualquier decisión.",
  },
  { codigo: "vinc10", question: "La gente ama trabajar en esta organización." },
  {
    codigo: "inn59",
    question:
      "Aquí constantemente se están buscando formas de hacer mejor las cosas.",
  },
  {
    codigo: "seg41",
    question:
      "En esta organización la seguridad y salud en el trabajo son poco importantes.",
  },
  {
    codigo: "lid87",
    question: "Mi jefe es una persona orientada a los resultados.",
  },
  {
    codigo: "vinc13",
    question: "Las personas se identifican con la organización.",
  },
  {
    codigo: "serv72",
    question: "Aquí se conoce muy bien lo que esperan los clientes/usuarios.",
  },
];

export default {
  data() {
    return {
      anteriorshow: true,
      employeeresponded: {},
      pase: 0,
      page: 1,
      perPage: 10,
      numberOfPages: 0,
      responses: {
        agree: true,
        comentarios: null,
      },
      scaleOptions: SCALE_OPTIONS,
      // Para la vista de instrucciones (descripción de la escala)
      scaleLabels: SCALE_OPTIONS.map(function(o) {
        return { value: o.value, label: o.label };
      }),
      questions: QUESTIONS,
    };
  },

  computed: {
    ...mapState(["isMovil"]),
    hasEmployee() {
      return Object.keys(this.employeeresponded).length > 0;
    },
    displayedQuestions() {
      const from = (this.page - 1) * this.perPage;
      return this.questions.slice(from, from + this.perPage);
    },
  },

  methods: {
    // ── Validaciones de botones ──────────────────────────────────────────────
    disabledbutton() {
      // Todas las preguntas de la página actual deben estar respondidas
      const answered = Object.keys(this.responses).length - 2;
      return answered < this.page * this.perPage;
    },
    disabledbutton1() {
      // Última página: permite hasta 5 sin responder
      const answered = Object.keys(this.responses).length - 2;
      return answered < this.page * this.perPage - 5;
    },

    // ── Carga de datos ───────────────────────────────────────────────────────
    async findresponded() {
      try {
        const result = await apiServices.get(
          `/employeeresponded/${this.$route.params.id}`,
        );

        if (!result.data) return;

        if (result.data.message) {
          this.$store.dispatch("notify", {
            message: result.data.message,
            type: "is-danger",
            duration: 4000,
            position: "top-center",
          });
          setTimeout(function() {
            window.location.replace("https://sietegho.com");
          }, 5000);
          return;
        }

        if (result.data.status === "Terminada") {
          this.$router.replace({ name: "finencuestaclima" });
          setTimeout(function() {
            window.location.replace("https://sietegho.com");
          }, 5000);
          return;
        }

        this.employeeresponded = result.data;
        this.page = result.data.encuesta ? result.data.encuesta.page : 1;
        this.pase = result.data.encuesta ? result.data.encuesta.pase : 0;
        if (result.data.responses) {
          this.responses = result.data.responses;
        }
      } catch (err) {
        console.log(err);
        this.$store.dispatch("notify", {
          message:
            "Ocurrió un error cargando la encuesta. Contáctese con Siete.",
          type: "is-danger",
          duration: 4000,
          position: "top-center",
        });
      }
    },

    // ── Navegación entre pases ───────────────────────────────────────────────
    next() {
      this.pase += 1;
      this.saveResponses();
      this.$nextTick(() => this.scrollTop());
    },
    nextquestions() {
      this.page++;
      this.saveResponses();
      this.$nextTick(() => this.scrollTop());
    },
    previousPase() {
      this.pase--;
      this.$nextTick(() => this.scrollTop());
    },
    previousPage() {
      this.page--;
      this.$nextTick(() => this.scrollTop());
    },

    // ── Finalización ─────────────────────────────────────────────────────────
    end() {
      this.saveResponses(true);
      this.$store.dispatch("notify", {
        message: "Perfecto. Encuesta finalizada con éxito.",
      });
      this.$router.replace({ name: "finencuestaclima" });
      setTimeout(function() {
        window.location.replace("https://sietegho.com");
      }, 6000);
    },
    endno() {
      this.anteriorshow = false;
      this.pase = 3;
      this.saveResponses(true);
    },

    // ── Guardado (respeta estructura original del endpoint) ──────────────────
    async saveResponses(end) {
      const payload = {
        id: this.$route.params.id,
        responses: this.responses,
        encuesta: {
          pase: this.pase,
          page: this.page,
        },
      };
      if (end) payload.end = true;

      try {
        await apiServices.post("/saveresponsesclima", payload);
      } catch (err) {
        console.log(err);
        this.$store.dispatch("notify", {
          message:
            "Ocurrió un error guardando las respuestas. Inténtelo más tarde.",
          type: "is-danger",
          position: "top-center",
        });
      }
    },

    scrollTop() {
      window.scrollTo({ behavior: "smooth", left: 0, top: 0 });
    },
  },

  created() {
    this.findresponded();
    this.numberOfPages = Math.ceil(this.questions.length / this.perPage);
    this.questions.forEach(function(question, index) {
      question.numeroPregunta = index + 1;
    });
  },
};
</script>

<style lang="scss">
// ── Variables de color (alineadas al dark de Bulma) ───────────────────────────
$color-dark: #363636;
$color-alt-row: #f0f0f0;
$color-accent: #5e2ca5; // purple usado en la progress bar actual

// ── Pill flotante sobre la caja ──────────────────────────────────────────────
.survey-tag-wrap {
  margin-bottom: -0.75rem; // se mete visualmente sobre el borde superior de la caja
  position: relative;
  z-index: 1;
}

// ── Caja principal ────────────────────────────────────────────────────────────
.survey-box {
  max-width: 960px;
  margin: 0 auto;
  border-radius: 10px;
  padding: 2.5rem;
}

// ── Hero fullheight cuando no hay datos ──────────────────────────────────────
.hero-fullheight {
  height: 100vh;

  .hero-body {
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

// ── Etiqueta de sección (pill superior) ──────────────────────────────────────
.survey-tag {
  display: inline-block;
  background: $color-dark;
  color: #fff;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  padding: 0.25rem 0.85rem;
  border-radius: 20px;
}

// ── Bloque de consentimiento (fondo diferenciado) ────────────────────────────
.consent-block {
  background: #f5f5f5;
  border-radius: 8px;
  padding: 1.25rem 1.5rem;
  margin-bottom: 0.75rem;

  p {
    font-size: 0.9rem;
    color: #555;
    line-height: 1.65;
    margin-bottom: 0.6rem;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

.consent-radios {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1.5rem;
}

// ── Escala en instrucciones: grilla 3 columnas ───────────────────────────────
.scale-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.6rem;
  max-width: 560px;
  margin: 0 auto;
}

.scale-grid-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: #f5f5f5;
  border-radius: 6px;
  padding: 0.5rem 0.75rem;
}

.scale-grid-item--nose {
  justify-content: center;
}

.scale-grid-value {
  font-size: 1.1rem;
  font-weight: 700;
  color: $color-dark;
  min-width: 18px;
  flex-shrink: 0;
}

.scale-grid-label {
  font-size: 0.78rem;
  color: #555;
  line-height: 1.3;
}

@media (max-width: 600px) {
  .scale-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 380px) {
  .scale-grid {
    grid-template-columns: 1fr;
  }
}

// ── Fila de pregunta (texto + escala de respuesta) ────────────────────────────
.question-row {
  margin-bottom: 1.75rem;

  &:last-child {
    margin-bottom: 0;
  }

  &__text {
    font-size: 0.9rem;
    line-height: 1.45;
    margin: 0 0 0.75rem;
  }

  &__num {
    font-weight: 700;
    margin-right: 0.2rem;
  }
}

// ── Tarjetas seleccionables (escala de respuesta) ─────────────────────────────
// Los b-radio-button de Buefy renderizan como <label class="button ...">
// dentro de .option-grid; se estiliza por descendencia para no depender de
// que una clase propia llegue exactamente al elemento raíz del componente.
.option-grid {
  display: grid;
  gap: 0.5rem;

  &--full {
    grid-template-columns: repeat(6, 1fr);
  }

  // Móvil: una opción por fila, número + etiqueta en horizontal para que
  // las etiquetas largas se lean bien y los botones sean fáciles de tocar.
  &--compact {
    grid-template-columns: 1fr;
    gap: 0.4rem;

    .button {
      flex-direction: row;
      align-items: center;
      justify-content: flex-start;
      gap: 0.75rem;
      min-height: 48px;
      padding: 0.6rem 0.85rem;
      text-align: left;
    }

    .option-card__value {
      flex: 0 0 1.5rem;
      text-align: center;
      font-size: 1.15rem;
    }

    .option-card__label {
      font-size: 0.85rem;
    }
  }

  .button {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.2rem;
    height: auto;
    min-height: 64px;
    padding: 0.5rem 0.4rem;
    border-radius: 8px;
    white-space: normal;
    text-align: center;
    transition: background-color 0.15s, border-color 0.15s;
    background-color: #f2f2f2 !important;
    border: 1px solid transparent !important;
    color: $color-dark !important;
    box-shadow: none !important;

    &:hover {
      background-color: #e2e2e2 !important;
    }

    &.is-selected {
      background-color: rgba($color-accent, 0.12) !important;
      border-color: $color-accent !important;
      color: $color-dark !important;

      &:hover {
        background-color: rgba($color-accent, 0.18) !important;
      }
    }
  }
}

.option-card__value {
  display: block;
  font-size: 1.05rem;
  font-weight: 800;
  line-height: 1;
}

.option-card__label {
  display: block;
  font-size: 0.66rem;
  line-height: 1.2;
}

// ── Progress ──────────────────────────────────────────────────────────────────
.progress-wrap {
  margin-top: 0;
}

.progress-info {
  display: flex;
  justify-content: space-between;
  font-size: 0.8rem;
  color: #888;
  margin-bottom: 0.3rem;
}

// ── Footer fijo (progreso + navegación) ───────────────────────────────────────
.survey-footer {
  position: fixed;
  left: 0;
  bottom: 0;
  width: 100%;
  background: #fff;
  border-top: 1px solid #e5e5e5;
  padding: 0.85rem 1.5rem;
  z-index: 30;

  &__inner {
    max-width: 960px;
    margin: 0 auto;
  }
}

.footer-spacer {
  height: 130px;
}

// ── Instrucciones ─────────────────────────────────────────────────────────────
.instructions-content {
  max-width: 680px;
  margin: 0 auto;

  p {
    margin-bottom: 1rem;
    line-height: 1.65;
    color: #444;
  }
}

// ── Utilitarios ───────────────────────────────────────────────────────────────
.mb-4 {
  margin-bottom: 1rem;
}
.mb-5 {
  margin-bottom: 1.5rem;
}
.mt-2 {
  margin-top: 0.5rem;
}
.mt-3 {
  margin-top: 0.75rem;
}
.mt-4 {
  margin-top: 1rem;
}
.mt-5 {
  margin-top: 1.5rem;
}
.ml-4 {
  margin-left: 1rem;
}
</style>



// WEBPACK FOOTER //
// src/components/clima/EncuestaClima.vue